import { useEffect, useMemo, useState } from "react";
import { fnbAPI, TABLE_STATUS_STYLE, fmtINR } from "./fnbApi";
import { toast } from "sonner";
import { Plus, X, Users, Pencil, Trash2 } from "lucide-react";
import { FormField, errorRing } from "../../components/form/FormField";
import { useFormValidation } from "../../hooks/useFormValidation";
import { required, minNumber, maxNumber } from "../../lib/validators";
import { NumberInput } from "@/components/ui/number-input";

const STATUS_OPTIONS = ["available", "occupied", "reserved", "cleaning", "waiting_bill", "blocked"];

export default function FnbTables() {
  const [tables, setTables] = useState([]);
  const [orders, setOrders] = useState([]);
  const [dialog, setDialog] = useState(null);   // null | { mode: "add" } | { mode: "edit", table }
  const [floorFilter, setFloorFilter] = useState("");

  const load = () => Promise.all([
    fnbAPI.tables(),
    fnbAPI.orders({ status: "open", limit: 100 }),
  ]).then(([t, o]) => { setTables(t); setOrders(o); });
  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, []);

  const floors = useMemo(() => [...new Set(tables.map(t => t.floor))], [tables]);
  const filtered = useMemo(() => floorFilter ? tables.filter(t => t.floor === floorFilter) : tables, [tables, floorFilter]);
  const orderByTable = useMemo(() => Object.fromEntries(orders.filter(o => o.table_id).map(o => [o.table_id, o])), [orders]);

  const changeStatus = async (t, status) => {
    try { await fnbAPI.updateTable(t.id, { status }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deleteTable = async (t) => {
    if (!window.confirm(`Delete Table ${t.table_number}? This cannot be undone.`)) return;
    try {
      await fnbAPI.deleteTable(t.id);
      toast.success(`Table ${t.table_number} deleted`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="fnb-tables-page">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setFloorFilter("")}
          className={`px-3 py-1.5 rounded-lg text-[12px] font-medium ${!floorFilter ? "bg-hocp-mint text-hocp-deepTeal" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
          data-testid="fnb-floor-all">All Floors</button>
        {floors.map(f => (
          <button key={f} onClick={() => setFloorFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-[12px] font-medium ${floorFilter === f ? "bg-hocp-mint text-hocp-deepTeal" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
            data-testid={`fnb-floor-${f}`}>{f}</button>
        ))}
        <div className="flex-1"/>
        <div className="flex flex-wrap items-center gap-3 text-[11.5px] text-slate-500">
          {Object.entries(TABLE_STATUS_STYLE).map(([k, v]) => (
            <div key={k} className="flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${v.dot}`}/>{v.label}</div>
          ))}
        </div>
        <button onClick={() => setDialog({ mode: "add" })} data-testid="fnb-add-table-btn"
          className="px-3 py-1.5 rounded-lg bg-hocp-mint text-hocp-deepTeal text-[12px] font-medium inline-flex items-center gap-1.5"><Plus className="w-3.5 h-3.5"/>Add Table</button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
        {filtered.map(t => {
          const st = TABLE_STATUS_STYLE[t.status] || TABLE_STATUS_STYLE.available;
          const ord = orderByTable[t.id];
          const canDelete = !ord && t.status !== "occupied";
          return (
            <div key={t.id} data-testid={`fnb-table-${t.table_number}`}
              className={`bg-white border border-slate-200 rounded-2xl p-3 hover:shadow-md transition-all relative overflow-hidden`}>
              <div className={`absolute top-0 right-0 w-2 h-full ${st.dot}`}/>
              <div className="flex items-start justify-between gap-1">
                <div className="font-display text-[20px] font-bold text-hocp-navy tabular-nums">{t.table_number}</div>
                <div className="flex items-center gap-0.5">
                  <button onClick={() => setDialog({ mode: "edit", table: t })}
                    data-testid={`fnb-table-edit-${t.table_number}`}
                    className="w-6 h-6 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-hocp-navy"
                    title="Edit"><Pencil className="w-3.5 h-3.5"/></button>
                  <button onClick={() => canDelete && deleteTable(t)} disabled={!canDelete}
                    data-testid={`fnb-table-delete-${t.table_number}`}
                    title={canDelete ? "Delete" : "Cannot delete — table has an active order"}
                    className={`w-6 h-6 rounded-md flex items-center justify-center ${canDelete ? "hover:bg-rose-50 text-slate-500 hover:text-rose-600" : "text-slate-300 cursor-not-allowed"}`}>
                    <Trash2 className="w-3.5 h-3.5"/></button>
                </div>
              </div>
              <div className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-500 font-medium">{t.floor}</div>
              <div className="mt-1 flex items-center gap-1 text-[11px] text-slate-500">
                <Users className="w-3 h-3"/>{t.capacity}
                {t.section && <span>· {t.section}</span>}
              </div>
              <span className={`inline-flex items-center gap-1.5 mt-2 px-2 py-0.5 rounded-md ring-1 text-[10.5px] font-medium ${st.chip}`}>{st.label}</span>
              {ord && (
                <div className="mt-2 pt-2 border-t border-slate-100 text-[11px]">
                  <div className="font-mono text-hocp-navy">{ord.code}</div>
                  <div className="text-slate-600 font-mono">{fmtINR(ord.total)}</div>
                </div>
              )}
              <select value={t.status} onChange={e => changeStatus(t, e.target.value)}
                data-testid={`fnb-table-status-${t.table_number}`} aria-label={`Status for table ${t.table_number}`}
                className="mt-2 w-full text-[11px] border border-slate-200 rounded-md px-1.5 py-1 bg-white">
                {STATUS_OPTIONS.map(o => <option key={o} value={o}>{TABLE_STATUS_STYLE[o]?.label || o}</option>)}
              </select>
            </div>
          );
        })}
      </div>
      {dialog && <TableDialog mode={dialog.mode} initial={dialog.table}
        onClose={() => setDialog(null)}
        onSaved={() => { setDialog(null); load(); }}/>}
    </div>
  );
}

function TableDialog({ mode, initial, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [f, setF] = useState({
    table_number: initial?.table_number || "",
    capacity: initial?.capacity || 4,
    floor: initial?.floor || "GF",
    section: initial?.section || "",
  });
  const [busy, setBusy] = useState(false);
  const v = useFormValidation({
    table_number: [required("Table number is required")],
    capacity: [minNumber(1, "Must seat at least 1"), maxNumber(40, "Max 40 seats")],
  });
  const submit = async (e) => {
    e.preventDefault();
    if (!v.validateAll(f)) return;
    setBusy(true);
    try {
      if (isEdit) {
        await fnbAPI.updateTable(initial.id, f);
        toast.success(`Table ${f.table_number} updated`);
      } else {
        await fnbAPI.createTable(f);
        toast.success("Table added");
      }
      onSaved();
    } catch (er) { toast.error(er?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid={isEdit ? "fnb-edit-table-dialog" : "fnb-add-table-dialog"}>
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display text-[16px] font-semibold text-hocp-navy">{isEdit ? `Edit Table ${initial.table_number}` : "Add Table"}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400"/></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3" noValidate>
          <FormField label="Table number" htmlFor="fnb-table-num" required error={v.fieldError("table_number")}>
            <input id="fnb-table-num" value={f.table_number} onChange={e => setF({...f, table_number: e.target.value})} onBlur={() => v.handleBlur("table_number", f.table_number)}
              placeholder="e.g. 12" data-testid="fnb-table-num"
              className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] ${errorRing(v.fieldError("table_number"))}`}/>
          </FormField>
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Seating capacity" htmlFor="fnb-table-cap" error={v.fieldError("capacity")}>
              <NumberInput id="fnb-table-cap" min="1" max="40" value={f.capacity} onChange={e => setF({...f, capacity: Number(e.target.value)})} onBlur={() => v.handleBlur("capacity", f.capacity)}
                placeholder="e.g. 4" data-testid="fnb-table-cap"
                className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] ${errorRing(v.fieldError("capacity"))}`}/>
            </FormField>
            <FormField label="Floor" htmlFor="fnb-table-floor" optional>
              <input id="fnb-table-floor" value={f.floor} onChange={e => setF({...f, floor: e.target.value})}
                placeholder="e.g. GF" data-testid="fnb-table-floor"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"/>
            </FormField>
          </div>
          <FormField label="Section" htmlFor="fnb-table-section" optional>
            <input id="fnb-table-section" value={f.section} onChange={e => setF({...f, section: e.target.value})}
              placeholder="e.g. Window / Corner" data-testid="fnb-table-section"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"/>
          </FormField>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-[13px] text-slate-600">Cancel</button>
            <button type="submit" disabled={busy} data-testid="fnb-table-submit"
              className="px-5 py-2 rounded-lg bg-hocp-mint text-hocp-deepTeal text-[13px] font-semibold">
              {busy ? "Saving…" : (isEdit ? "Save" : "Add")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
